// src/stations/geometry/geometryStation.ts — Tactile Marine Geometry Sandbox (Epic 3, FR-GEO-001..004)
import { tokens } from '@ui/designTokens';
import { ClaweyHud } from '@ui/components/claweyHud';
import { SoundEngine } from '@audio/soundEngine';
import { ClawProtractor } from './clawProtractor';
import { BenthicMirrorLine } from './mirrorLine';
import { GeometryStorage } from '@storage/geometryStorage';
import { SocraticTutorDrawer } from '@ai/socraticTutorDrawer';

export type GeometryActiveTab = 'protractor' | 'mirror';

export class GeometryStation {
  private container: HTMLElement;
  private claweyHud?: ClaweyHud;
  private sound: SoundEngine;
  private activeTab: GeometryActiveTab = 'protractor';

  private protractor: ClawProtractor;
  private mirrorLine: BenthicMirrorLine;
  private drawer?: SocraticTutorDrawer;

  constructor(claweyHud?: ClaweyHud, sound?: SoundEngine) {
    this.container = document.createElement('div');
    this.container.className = 'tactile-geometry-station';
    this.claweyHud = claweyHud;
    this.sound = sound || new SoundEngine();

    this.protractor = new ClawProtractor(this.sound, this.claweyHud);
    this.mirrorLine = new BenthicMirrorLine(this.sound, this.claweyHud);
  }

  render(): HTMLElement {
    this.updateView();
    return this.container;
  }

  private updateView(): void {
    const progress = GeometryStorage.load();
    const soundMuted = this.sound.isMuted();

    this.container.innerHTML = `
      <!-- Station Banner -->
      <section class="morris-card morris-card-gold" style="padding: 28px 24px; margin-bottom: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase; margin-bottom: 6px;">
              <span>📐</span> KS2 Year 4 · Spatial Workshop
            </div>
            <h1 style="font-size: 2.2rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 8px;">
              Tactile Marine Geometry Sandbox
            </h1>
            <p style="font-size: 1.05rem; color: #3A4D66; max-width: 620px; line-height: 1.6; margin: 0;">
              Direct touch manipulation of angles, degrees, and reflective symmetry with Clawey's naturalist measuring instruments.
            </p>
          </div>

          <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
            <button id="btn-geo-socratic-hint" class="touch-target" style="padding: 8px 16px; font-size: 0.95rem; background: #FFFDF9; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px; font-weight: 700; color: ${tokens.color.morrisIndigo}; cursor: pointer;">
              💡 Ask Clawey for Socratic Guidance
            </button>
            <button id="geo-sound-toggle" class="touch-target" style="padding: 8px 16px; font-size: 0.95rem; background: #FFFDF9; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 20px;">
              ${soundMuted ? '🔇 Sound Off' : '🔊 Sound On'}
            </button>
          </div>
        </div>

        <!-- Telemetry Stats Ribbon -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-top: 20px;">
          <div style="background: rgba(255, 253, 249, 0.9); padding: 12px 16px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">ANGLES MASTERED</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">📐 ${progress.anglesSolved}</div>
          </div>
          <div style="background: rgba(255, 253, 249, 0.9); padding: 12px 16px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">REFLECTIONS SOLVED</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.madderCoral};">🪞 ${progress.reflectionsSolved}</div>
          </div>
          <div style="background: rgba(255, 253, 249, 0.9); padding: 12px 16px; border-radius: 12px; border: 1.5px solid rgba(229, 169, 60, 0.4);">
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 700;">BEST STREAK</div>
            <div style="font-size: 1.8rem; font-weight: 800; color: ${tokens.color.gildedBrass};">🔥 ${progress.bestAngleStreak}</div>
          </div>
        </div>
      </section>

      <!-- Instrument Navigation Tabs -->
      <div style="display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap;">
        <button
          id="tab-btn-protractor"
          class="touch-target"
          style="
            padding: 14px 24px;
            font-size: 1.05rem;
            font-weight: 800;
            border-radius: 14px;
            border: 2px solid ${this.activeTab === 'protractor' ? tokens.color.gildedBrass : 'rgba(7, 20, 36, 0.15)'};
            background: ${this.activeTab === 'protractor' ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
            color: ${tokens.color.morrisIndigo};
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          "
        >
          🦞 Claw Protractor (Angles & Degrees)
        </button>

        <button
          id="tab-btn-mirror"
          class="touch-target"
          style="
            padding: 14px 24px;
            font-size: 1.05rem;
            font-weight: 800;
            border-radius: 14px;
            border: 2px solid ${this.activeTab === 'mirror' ? tokens.color.gildedBrass : 'rgba(7, 20, 36, 0.15)'};
            background: ${this.activeTab === 'mirror' ? 'rgba(255, 246, 229, 0.95)' : '#FFFDF9'};
            color: ${tokens.color.morrisIndigo};
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          "
        >
          🪞 Benthic Mirror Line (Reflections)
        </button>
      </div>

      <!-- Active Tool Stage Mount -->
      <div id="geometry-tool-mount"></div>
    `;

    // Mount active instrument tool
    const mount = this.container.querySelector('#geometry-tool-mount');
    if (mount) {
      mount.appendChild(
        this.activeTab === 'protractor'
          ? this.protractor.render()
          : this.mirrorLine.render()
      );
    }

    this.attachListeners();
  }

  private attachListeners(): void {
    // Sound toggle
    const soundBtn = this.container.querySelector<HTMLButtonElement>('#geo-sound-toggle');
    soundBtn?.addEventListener('click', () => {
      const isMuted = this.sound.toggleMute();
      soundBtn.textContent = isMuted ? '🔇 Sound Off' : '🔊 Sound On';
      this.sound.playTap();
    });

    // Socratic Guidance button
    const hintBtn = this.container.querySelector<HTMLButtonElement>('#btn-geo-socratic-hint');
    hintBtn?.addEventListener('click', () => {
      this.sound.playTap();
      if (!this.drawer) {
        this.drawer = new SocraticTutorDrawer();
        this.container.appendChild(this.drawer.getElement());
      }
      this.drawer.openWithRequest({
        questionId: `geo_${this.activeTab}`,
        domain: 'geometry',
        topic: this.activeTab === 'protractor' ? 'Angles and Protractor' : 'Reflections and Symmetry',
        prompt:
          this.activeTab === 'protractor'
            ? 'How do I read acute vs obtuse angles with Clawey’s protractor?'
            : 'How do I reflect coordinates across a vertical mirror line?',
      });
    });

    // Tab buttons
    const tabProt = this.container.querySelector<HTMLButtonElement>('#tab-btn-protractor');
    tabProt?.addEventListener('click', () => {
      if (this.activeTab !== 'protractor') {
        this.activeTab = 'protractor';
        this.sound.playTap();
        this.updateView();
      }
    });

    const tabMirror = this.container.querySelector<HTMLButtonElement>('#tab-btn-mirror');
    tabMirror?.addEventListener('click', () => {
      if (this.activeTab !== 'mirror') {
        this.activeTab = 'mirror';
        this.sound.playTap();
        this.updateView();
      }
    });
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
